// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused".
// The hub import warmed the Rematch model cluster; this specifier restores
// that init order. It retires with the Z2 codemod.
import '@/pages/api/scribeAPIV2';

import { menuCoachmarkStepAtom } from '@/atoms/menuCoachmarkAtoms';
import { sidebarPanelContentAtom } from '@/atoms/sidebarPanelAtoms';
import { Button } from '@heidi/design-system/button';
import {
  Spotlight,
  SpotlightAnchor,
  SpotlightArrow,
  SpotlightClose,
  SpotlightContent,
  SpotlightDescription,
  SpotlightFooter,
  SpotlightHeader,
  SpotlightProgress,
  SpotlightTitle,
} from '@heidi/design-system/spotlight';
import { ModalId } from '@heidi/schema/ml-scribe';
import { useMutateMarkModalAsViewed } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { sharedMessages } from './coachmark.messages';
import {
  PANEL_COLLAPSE_SETTLE_MS,
  SPOTLIGHT_FADE_OUT_MS,
  TOTAL_STEPS,
} from './constants';

const messages = defineMessages({
  title: {
    defaultMessage: 'Your sessions are still here',
    id: '55sO7GaW7e',
    description: 'Coachmark title explaining where the session list went',
  },
  body: {
    defaultMessage: 'Hover over the menu to see your sessions again',
    id: 'zdhQXPGGeH',
    description: 'Coachmark body explaining how to reveal the session list',
  },
});

interface MenuCoachmarkHoverStepProps {
  children?: ReactNode;
}

/**
 * Step 2 of the menu coachmark tour.
 * Self-contained: reads atom state directly, provides its own handlers.
 * Shows how to hover over the primary nav to reveal the session list.
 *
 * Uses a virtual anchor aligned to the icon center of the wrapped NavItem
 * so the arrow points at the icon rather than the full nav-item center.
 *
 * Auto-advances to step 3 if the user hovers to reveal the secondary panel
 * (sidebarPanelContentAtom becomes truthy) while the spotlight is visible.
 *
 * Figma: node 1345:80339
 */
export const MenuCoachmarkHoverStep = ({ children }: MenuCoachmarkHoverStepProps) => {
  const currentStep = useAtomValue(menuCoachmarkStepAtom);
  const hoverContent = useAtomValue(sidebarPanelContentAtom);
  const setStep = useSetAtom(menuCoachmarkStepAtom);
  const { mutate: markModalAsViewed } = useMutateMarkModalAsViewed();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [iconCenterY, setIconCenterY] = useState(22);
  const transitionTimersRef = useRef<number[]>([]);

  const isStep2 = currentStep === 2;

  // Measure the icon center relative to the wrapper so the virtual anchor
  // aligns the arrow with the icon, not the full nav-item center.
  useEffect(() => {
    if (!isStep2 || !wrapperRef.current) return;
    const svg = wrapperRef.current.querySelector('svg');
    if (!svg) return;
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const svgRect = svg.getBoundingClientRect();
    setIconCenterY(svgRect.top - wrapperRect.top + svgRect.height / 2);
  }, [isStep2]);

  // Defer showing spotlight until layout has settled after panel collapse
  const [visibleOpen, setVisibleOpen] = useState(false);

  useEffect(() => {
    if (!isStep2) {
      setVisibleOpen(false);
      return;
    }
    const timeoutId = window.setTimeout(
      () => setVisibleOpen(true),
      PANEL_COLLAPSE_SETTLE_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isStep2]);

  // Clean up timers on unmount
  useEffect(() => {
    const timers = transitionTimersRef.current;
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const clearPendingTimers = useCallback(() => {
    transitionTimersRef.current.forEach((id) => window.clearTimeout(id));
    transitionTimersRef.current = [];
  }, []);

  const advanceFromStep2 = useCallback(() => {
    const timer = window.setTimeout(() => {
      setStep(3);
    }, SPOTLIGHT_FADE_OUT_MS);
    transitionTimersRef.current.push(timer);
  }, [setStep]);

  // Auto-advance: if the user hovers to reveal the secondary panel while the
  // spotlight is visible, fade out and advance to step 3 (Create).
  useEffect(() => {
    if (isStep2 && visibleOpen && hoverContent) {
      setVisibleOpen(false);
      advanceFromStep2();
    }
  }, [isStep2, visibleOpen, hoverContent, advanceFromStep2]);

  const handleNext = useCallback(() => {
    clearPendingTimers();
    setVisibleOpen(false);
    advanceFromStep2();
  }, [clearPendingTimers, advanceFromStep2]);

  const handleDismiss = useCallback(() => {
    markModalAsViewed({
      modal_id: ModalId.MENU_COACHMARK_TOUR,
      increment_count: true,
    });
  }, [markModalAsViewed]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        clearPendingTimers();
        setVisibleOpen(false);
        setStep(null);
      }
    },
    [setStep, clearPendingTimers]
  );

  return (
    <div ref={wrapperRef} className="relative">
      {children}
      {isStep2 && (
        <Spotlight open={visibleOpen} onOpenChange={handleOpenChange}>
          <SpotlightAnchor asChild>
            <div
              className="pointer-events-none absolute left-0 h-px w-full"
              style={{ top: iconCenterY }}
              aria-hidden="true"
            />
          </SpotlightAnchor>
          <SpotlightContent
            side="right"
            sideOffset={12}
            align="start"
            alignOffset={-16}
            arrowPadding={8}
            data-testid="menu-coachmark-step-2"
            onInteractOutside={(e) => e.preventDefault()}
          >
            <SpotlightArrow width={14} height={9} />
            <SpotlightHeader>
              <SpotlightTitle>
                <FormattedMessage {...messages.title} />
              </SpotlightTitle>
              <SpotlightClose
                onClick={handleDismiss}
                data-testid="menu-coachmark-step-2-close"
              />
            </SpotlightHeader>
            <SpotlightDescription>
              <FormattedMessage {...messages.body} />
            </SpotlightDescription>
            <SpotlightFooter>
              <SpotlightProgress>
                <FormattedMessage
                  {...sharedMessages.stepProgress}
                  values={{ current: 2, total: TOTAL_STEPS }}
                />
              </SpotlightProgress>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleNext}
                className="bg-surface text-text-primary hover:bg-background-secondary"
                data-testid="menu-coachmark-step-2-next"
              >
                <FormattedMessage {...sharedMessages.next} />
              </Button>
            </SpotlightFooter>
          </SpotlightContent>
        </Spotlight>
      )}
    </div>
  );
};
